import { DialogSize } from '@rio-cloud/rio-uikit/components/dialog/Dialog';
import { OPEN_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { config } from '@/config';
import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getLocale } from '@/configuration/lang/langSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { getAccessToken } from '@/configuration/tokenHandling/tokenSlice';
import { MFE_DIALOG_PREFIX } from '@/features/common/components/dialogs/dialogConstants';

type MessageHandler = (event: MessageEvent<unknown>) => void;
type OriginFilteredMessageHandler = {
    origin: string;
    handler: MessageHandler;
};
type ActionMessageHandler = (payload: any) => void;
type ActionMessageHandlerDeclaration = {
    origin?: string;
    handlers: Record<string, ActionMessageHandler>;
};
type IncomingPostMessageOptions = MessageHandler | OriginFilteredMessageHandler | ActionMessageHandlerDeclaration;

export type RemoteDialogProps = {
    path: string;
    children: React.ReactNode;
    size?: DialogSize;
    className?: string;
    onReceiveMessage?: IncomingPostMessageOptions;
};

export const buildUrl = (path: string, locale: string, token: string) => {
    const finalUrl = new URL(config.mfeBaseUrl);
    finalUrl.pathname = path;
    finalUrl.searchParams.set('wu_token', token);
    finalUrl.searchParams.set('wu_locale', locale);
    return finalUrl.toString();
};

export const RemoteDialog = ({
    path,
    children,
    size = 'md',
    className = 'cursor-pointer hover-text-underline',
    onReceiveMessage = (event: MessageEvent<unknown>) => null,
}: RemoteDialogProps) => {
    const { sendMessageToParent } = usePostMessageSender();
    const token = useAppSelector(getAccessToken);
    const locale = useAppSelector(getLocale) || DEFAULT_LOCALE;

    const handleClick = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${OPEN_DIALOG_EVENT}`,
            payload: {
                src: buildUrl(path, locale, token),
                dialogProps: {
                    bsSize: size,
                },
            },
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    return (
        <span className={className} onClick={handleClick} tabIndex={0} onKeyDown={handleKeyDown} role="button">
            {children}
        </span>
    );
};
